import React, { useState, useMemo } from 'react';
import { Card } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useTranslation } from 'react-i18next';
import { calendarOrders, calendarStatusKey, localDateFromKey } from '../../../utils/dashboardOrders';
import { getDeliveryStatusBadgeClass } from '../Orders/deliveryStatusConstants';

export default function DeliveryCalendar({ orders }) {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [selectedDate, setSelectedDate] = useState(() => new Date());

    const ordersWithDelivery = useMemo(() => calendarOrders(orders), [orders]);

    const deliveriesByDate = useMemo(() => {
        const map = {};
        ordersWithDelivery.forEach(o => {
            const key = (o.delivery_date || '').slice(0, 10);
            if (!map[key]) map[key] = [];
            map[key].push(o);
        });
        return map;
    }, [ordersWithDelivery]);

    const selectedDateKey = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`;
    const eventsForSelectedDate = deliveriesByDate[selectedDateKey] || [];

    return (
        <Card className="border-0 shadow-sm delivery-calendar-card">
            <Card.Header className="bg-white border-0 d-flex justify-content-between align-items-center">
                <h5 className="mb-0">
                    <i className="fa-solid fa-calendar-days me-2 text-primary" />
                    Order Deliveries
                </h5>
                <Link to="/orders" className="btn btn-sm btn-outline-primary">View all orders</Link>
            </Card.Header>
            <Card.Body className="p-0">
                {ordersWithDelivery.length === 0 ? (
                    <div className="text-center py-5 text-muted">
                        <i className="fa-solid fa-calendar-xmark fa-3x mb-3 opacity-50" />
                        <p className="mb-0">No upcoming deliveries</p>
                        <Link to="/orders/create" className="btn btn-primary mt-2">Create order</Link>
                    </div>
                ) : (
                    <div className="card my-calendar border-0">
                        <div className="card-body schedules-cal p-2 dz-calender">
                            <DatePicker
                                selected={selectedDate}
                                onChange={(date) => setSelectedDate(date)}
                                dateFormat="MM-dd-yyyy"
                                inline
                                className="form-control"
                                calendarStartDay={1}
                            />
                            <div className="events">
                                <h6>Events</h6>
                                <div className="dz-scroll event-scroll" style={{ maxHeight: 260, overflowY: 'auto' }}>
                                    {eventsForSelectedDate.length === 0 ? (
                                        <div className="py-4 text-center text-white-50 small">No events on this date</div>
                                    ) : (
                                        eventsForSelectedDate.map((o, idx) => {
                                            const d = localDateFromKey(o.delivery_date);
                                            const isLast = idx === eventsForSelectedDate.length - 1;
                                            return (
                                                <div key={o.id} className={`event-media ${isLast ? 'border-0' : ''}`}>
                                                    <div className="d-flex align-items-center" style={{ cursor: 'pointer' }} onClick={() => navigate(`/orders/${o.id}`)}>
                                                        <div className="event-box">
                                                            <h5 className="mb-0">{d.getDate()}</h5>
                                                            <span>{d.toLocaleDateString('en-GB', { weekday: 'short' })}</span>
                                                        </div>
                                                        <div className="event-data ms-2">
                                                            <h5 className="mb-0">Order #{o.order_number}</h5>
                                                            <span>{o.buyer_name || 'Tabliya'}</span>
                                                        </div>
                                                    </div>
                                                    <span className={`badge light border-0 badge-${getDeliveryStatusBadgeClass(o.delivery_status)}`} data-testid="calendar-status">
                                                        {t(`deliveryStatuses.${calendarStatusKey(o)}`)}
                                                    </span>
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </Card.Body>
        </Card>
    );
}
